import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

export interface EventThumbnail {
  base64: string;
  mimeType: string;
}

export interface Event {
  id: number;
  title: string;
  description: string;
  location: string;
  latitude: string;
  longitude: string;
  startDate: string;
  endDate: string;
  expectedAttendance: number;
  isVerified: boolean;
  isActive: boolean;
  createdBy: number;
  createdByRole: string;
  verifiedBy?: number;
  createdAt: string;
  updatedAt: string;
  isFeatured: boolean;
  featuredAt?: string;
  thumbnailBase64?: string;
  thumbnailMimeType?: string;
  thumbnail?: EventThumbnail;
  // Legacy fields for backward compatibility
  EVENT_ID?: number;
  TITLE?: string;
  DESCRIPTION?: string;
  LOCATION?: string;
  ADDRESS?: string;
  LATITUDE?: string | number;
  LONGITUDE?: string | number;
  START_DATE?: string;
  END_DATE?: string;
  IS_VERIFIED?: boolean;
  IS_FEATURED?: boolean;
  imageUrl?: string;
  IMAGE_URL?: string;
  images?: string[];
  IMAGES?: string[];
  CREATED_AT?: string;
  EXPECTED_ATTENDANCE?: number;
  [key: string]: any; // Allow for additional properties
}

export interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ApiResponse {
  events: Event[];
  pagination: PaginationData;
}

interface UseRoleBasedEventsReturn {
  events: Event[];
  loading: boolean;
  error: string | null;
  pagination: PaginationData | null;
  refetch: () => void;
}

/**
 * A hook that fetches events based on the user's role and selected source.
 * - COMMITTEE users default to 'my-events'
 * - COMMITTEE_ADMIN and ADMIN users default to 'public' events
 * 
 * @param source - Optional override for the event source ('public' or 'my')
 * @param params - Optional parameters for filtering events
 */
export const useRoleBasedEvents = (
  source?: 'public' | 'my',
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    category?: string;
    isFeatured?: boolean;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }
): UseRoleBasedEventsReturn => {
  const { user, token } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Determine the default source based on user role
  const defaultSource = useMemo(() => {
    if (!user) return 'public';
    
    if (user.role === 'COMMITTEE') {
      return 'my';
    } else if (['COMMITTEE_ADMIN', 'ADMIN', 'VENDOR_ADMIN'].includes(user.role)) {
      return 'public';
    }
    
    return 'public';
  }, [user]);

  // Use the provided source or fall back to the default
  const effectiveSource = source || defaultSource;

  const fetchEvents = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let response;
      
      if (effectiveSource === 'my' && token) {
        // Fetch user's own events (requires authentication)
        response = await apiService.getMyEvents(token, params);
      } else {
        // Fetch public events (no authentication required)
        response = await apiService.getPublicEvents(params);
      }
      
      // Handle the new response structure
      console.log('API Response:', response);
      
      if (response && response.events) {
        // New API response format with events array and pagination
        setEvents(response.events);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else if (Array.isArray(response)) {
        // Legacy format - direct array of events
        setEvents(response);
        setPagination(null);
      } else if (response && (response.data || response.events)) {
        // Legacy format - object with data or events property
        const eventsData = response.data || response.events || [];
        setEvents(eventsData);
        setPagination(null);
      } else {
        // Fallback for unexpected response format
        console.warn('Unexpected API response format:', response);
        setEvents([]);
        setPagination(null);
      }
    } catch (err) {
      console.error(`Failed to fetch ${effectiveSource} events:`, err);
      setError(err instanceof Error ? err.message : `Failed to fetch ${effectiveSource} events`);
      setEvents([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [
    user, 
    token, 
    effectiveSource, 
    params?.page, 
    params?.limit, 
    params?.search, 
    params?.startDate, 
    params?.endDate, 
    params?.category, 
    params?.isFeatured, 
    params?.sortBy, 
    params?.sortOrder
  ]);

  // Normalize events to a consistent format
  const normalizedEvents = useMemo(() => {
    return (events || []).map((e: Event) => {
      // Handle the thumbnail field which might be an object or direct base64 string
      let thumbnailBase64 = null;
      
      if (e.thumbnailBase64) {
        thumbnailBase64 = e.thumbnailBase64;
      } else if (e.thumbnail && e.thumbnail.base64) {
        thumbnailBase64 = e.thumbnail.base64;
      }
      
      return {
        id: e.id ?? e.EVENT_ID,
        title: e.title ?? e.TITLE ?? 'Untitled Event',
        description: e.description ?? e.DESCRIPTION ?? '',
        location: e.location ?? e.LOCATION ?? e.address ?? e.ADDRESS ?? '',
        date: e.startDate ?? e.START_DATE ?? e.createdAt ?? e.CREATED_AT ?? '',
        isFeatured: e.isFeatured ?? e.IS_FEATURED ?? false,
        isPopular: e.isPopular ?? false,
        status: e.isVerified ?? e.IS_VERIFIED ? 'approved' : 'pending',
        entryFee: e.entryFee,
        // Image fields
        thumbnailBase64,
        thumbnailMimeType: e.thumbnailMimeType ?? (e.thumbnail ? e.thumbnail.mimeType : null),
        imageUrl: e.imageUrl ?? e.IMAGE_URL ?? null,
        images: e.images ?? e.IMAGES ?? [],
        // Location coordinates
        latitude: e.latitude ?? e.LATITUDE,
        longitude: e.longitude ?? e.LONGITUDE,
        // Additional fields
        startDate: e.startDate ?? e.START_DATE,
        endDate: e.endDate ?? e.END_DATE,
        expectedAttendance: e.expectedAttendance ?? e.EXPECTED_ATTENDANCE,
        createdBy: e.createdBy,
        createdByRole: e.createdByRole,
        isActive: e.isActive ?? true,
        // Keep the original event data for reference
        originalEvent: e,
      };
    });
  }, [events]);

  return {
    events: normalizedEvents,
    loading,
    error,
    pagination,
    refetch: fetchEvents,
  };
};
